import {
  IconComponent,
  RouteRef,
  createExtension,
  createExtensionDataRef,
} from '@backstage/frontend-plugin-api';
import { convertLegacyRouteRef } from '@backstage/core-compat-api';
import HomeIcon from '@material-ui/icons/Home';
import CategoryIcon from '@material-ui/icons/Category';
import AccountTreeIcon from '@material-ui/icons/AccountTree';
import ExtensionIcon from '@material-ui/icons/Extension';
import LibraryBooks from '@material-ui/icons/LibraryBooks';
import CreateComponentIcon from '@material-ui/icons/AddCircleOutline';
import EmojiObjectsIcon from '@material-ui/icons/EmojiObjects';
import MoneyIcon from '@material-ui/icons/MonetizationOn';
import BuildIcon from '@material-ui/icons/Build';
import FileCopyIcon from '@material-ui/icons/FileCopy';
import { homePlugin } from '@backstage/plugin-home';
import { catalogPlugin } from '@backstage/plugin-catalog';
import { catalogGraphPlugin } from '@backstage/plugin-catalog-graph';
import { apiDocsPlugin } from '@backstage/plugin-api-docs';
import { techdocsPlugin } from '@backstage/plugin-techdocs';
import { scaffolderPlugin } from '@backstage/plugin-scaffolder';
import { techInsightsPlugin } from '@backstage-community/plugin-tech-insights';
import { costInsightsPlugin } from '@backstage-community/plugin-cost-insights';
import { devToolsPlugin } from '@backstage/plugin-devtools';
import { kaminoPlugin } from '@internal/plugin-kamino';

/**
 * The new frontend system derives nav items from PageBlueprint `title` and
 * `icon` params, but pages converted from legacy routes via
 * `convertLegacyAppRoot` carry neither. The `app/nav` extension still accepts
 * legacy nav-item data on its `items` input (matched to pages by route ref),
 * so this shim recreates the removed `NavItemBlueprint` to supply titles and
 * icons for converted pages.
 *
 * Delete this file once all legacy routes are migrated to plugins that
 * declare `title`/`icon` on their own pages.
 */
type LegacyNavItemTarget = {
  title: string;
  icon: IconComponent;
  routeRef: RouteRef<undefined>;
};

// Same data ref id that @backstage/plugin-app uses for its `items` input.
const legacyNavItemDataRef = createExtensionDataRef<LegacyNavItemTarget>().with(
  { id: 'core.nav-item.target' },
);

function makeLegacyNavItem(name: string, params: LegacyNavItemTarget) {
  return createExtension({
    kind: 'nav-item',
    name,
    attachTo: { id: 'app/nav', input: 'items' },
    output: [legacyNavItemDataRef],
    *factory() {
      yield legacyNavItemDataRef(params);
    },
  });
}

export const legacyPageNavItems = [
  makeLegacyNavItem('home', {
    title: 'Home',
    icon: HomeIcon,
    routeRef: convertLegacyRouteRef(homePlugin.routes.root),
  }),
  makeLegacyNavItem('catalog', {
    title: 'Catalog',
    icon: CategoryIcon,
    routeRef: convertLegacyRouteRef(catalogPlugin.routes.catalogIndex),
  }),
  makeLegacyNavItem('catalog-graph', {
    title: 'Catalog Graph',
    icon: AccountTreeIcon,
    routeRef: convertLegacyRouteRef(catalogGraphPlugin.routes.catalogGraph),
  }),
  makeLegacyNavItem('api-docs', {
    title: 'APIs',
    icon: ExtensionIcon,
    routeRef: convertLegacyRouteRef(apiDocsPlugin.routes.root),
  }),
  makeLegacyNavItem('docs', {
    title: 'Docs',
    icon: LibraryBooks,
    routeRef: convertLegacyRouteRef(techdocsPlugin.routes.root),
  }),
  makeLegacyNavItem('create', {
    title: 'Create...',
    icon: CreateComponentIcon,
    routeRef: convertLegacyRouteRef(scaffolderPlugin.routes.root),
  }),
  makeLegacyNavItem('tech-insights', {
    title: 'Tech Insights',
    icon: EmojiObjectsIcon,
    routeRef: convertLegacyRouteRef(techInsightsPlugin.routes.root),
  }),
  makeLegacyNavItem('cost-insights', {
    title: 'Cost Insights',
    icon: MoneyIcon,
    routeRef: convertLegacyRouteRef(costInsightsPlugin.routes.root),
  }),
  makeLegacyNavItem('devtools', {
    title: 'DevTools',
    icon: BuildIcon,
    routeRef: convertLegacyRouteRef(devToolsPlugin.routes.root),
  }),
  makeLegacyNavItem('kamino', {
    title: 'Kamino',
    icon: FileCopyIcon,
    routeRef: convertLegacyRouteRef(kaminoPlugin.routes.root),
  }),
];
